import { useState, useCallback } from "react";
import { DeviceInfo } from "../services/vision/types";
import { VisionService } from "../services/vision/visionService";
import { AIModel } from "../services/gemini/config";

// Create a new vision service instance
const visionService = new VisionService(AIModel.GEMINI);

interface UseImageProcessorProps {
  onError: (message: string) => void;
}

interface UseImageProcessorReturn {
  deviceInfo: Partial<DeviceInfo> | null;
  isLoading: boolean;
  analyzeImage: (imageDataUrl: string) => Promise<void>;
  resizeImageForStorage: (
    dataUrl: string,
    maxWidth?: number,
    maxSizeKB?: number
  ) => Promise<string>;
  getBase64ImageSizeKB: (base64String: string) => number;
  setDeviceInfo: React.Dispatch<
    React.SetStateAction<Partial<DeviceInfo> | null>
  >;
}

export const useImageProcessor = ({
  onError,
}: UseImageProcessorProps): UseImageProcessorReturn => {
  const [deviceInfo, setDeviceInfo] = useState<Partial<DeviceInfo> | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);

  // Handle analyzing the captured image
  const analyzeImage = useCallback(
    async (imageDataUrl: string) => {
      if (!imageDataUrl) return;

      setIsLoading(true);

      try {
        const info = await visionService.analyzeDeviceImage(imageDataUrl);
        setDeviceInfo(info);
        if (info.error) {
          onError(info.error);
        }
      } catch (err) {
        console.error("Error analyzing image:", err);
        onError(
          "Failed to analyze the image. Please try again or use a clearer image."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [onError]
  );

  // Function to resize an image to a smaller size for storage
  const resizeImageForStorage = useCallback(
    async (
      dataUrl: string,
      maxWidth = 600,
      maxSizeKB = 90
    ): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            const ratio = maxWidth / width;
            width = maxWidth;
            height = Math.floor(height * ratio);
          }

          // Create canvas and resize
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          // Draw image
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Try different quality settings until we get a small enough image
          const compressImage = (quality = 0.5): string => {
            // Convert to data URL with specified quality
            const resizedDataUrl = canvas.toDataURL("image/jpeg", quality);

            // Calculate approximate size in KB (base64 string length * 0.75 / 1024)
            // The 0.75 factor is because base64 encoding increases size by ~33%
            const sizeKB = Math.round(
              ((resizedDataUrl.length - resizedDataUrl.indexOf(",") - 1) *
                0.75) /
                1024
            );

            console.log(`Image size at quality ${quality}: ~${sizeKB}KB`);

            if (sizeKB > maxSizeKB && quality > 0.1) {
              // If still too large, try with lower quality
              return compressImage(quality - 0.1);
            }

            // If we're still above the target size at minimum quality,
            // try reducing dimensions further
            if (sizeKB > maxSizeKB && quality <= 0.1 && width > 300) {
              console.log("Reducing dimensions further to achieve target size");
              // Reduce dimensions by 25%
              width = Math.floor(width * 0.75);
              height = Math.floor(height * 0.75);

              // Resize canvas
              canvas.width = width;
              canvas.height = height;

              // Redraw at smaller size
              ctx.drawImage(img, 0, 0, width, height);

              // Try again with the smallest dimensions
              return compressImage(0.1);
            }

            return resizedDataUrl;
          };

          // Start compression with initial quality of 0.5
          const optimizedDataUrl = compressImage();
          resolve(optimizedDataUrl);
        };

        img.onerror = () => {
          reject(new Error("Failed to load image for resizing"));
        };

        img.src = dataUrl;
      });
    },
    []
  );

  // Helper function to estimate base64 image size in KB
  const getBase64ImageSizeKB = useCallback((base64String: string): number => {
    // Remove the data:image/jpeg;base64, part
    const base64WithoutPrefix = base64String.substring(
      base64String.indexOf(",") + 1
    );
    // Calculate size: base64 string length * 0.75 (to account for base64 encoding) / 1024 (to convert to KB)
    return Math.round((base64WithoutPrefix.length * 0.75) / 1024);
  }, []);

  return {
    deviceInfo,
    isLoading,
    analyzeImage,
    resizeImageForStorage,
    getBase64ImageSizeKB,
    setDeviceInfo,
  };
};
